import { DURATION_PRESETS_HOURS, MINUTE_STEP } from '@tk/shared';
import { Minus, Plus } from 'lucide-react';
import { useKaannos } from '../lib/i18n';

/**
 * Kesto puolen tunnin askelin (suunnitelma 10).
 *
 * Vapaata tekstisyöttöä ei ole tarkoituksella: kun arvoa ei voi kirjoittaa,
 * virheellistä arvoa ei voi syntyä, eikä puolen tunnin sääntöä tarvitse
 * selittää käyttäjälle virheilmoituksena.
 *
 * `minutes` saa olla `null`, jolloin lukemassa on viiva eikä lukua. Sitä
 * tarvitsee koneen ehdotuksen vahvistus (backlog 5): siinä kesto on se
 * ainoa asia, jota kone ei saanut sanoa, eikä valitsin saa näyttää
 * mitään valmiiksi valitulta. Esitäytetty luku olisi ankkuri, jonka ohi
 * harva korjaa.
 */
export default function KestoValitsin({
  minutes,
  onChange,
  maxMinutes = 24 * 60,
}: {
  minutes: number | null;
  onChange: (minutes: number) => void;
  maxMinutes?: number;
}) {
  const { t, m } = useKaannos();

  // Tyhjästä ensimmäinen askel osuu pienimpään sallittuun kestoon, ei
  // nollaan: nolla ei ole kelvollinen kirjaus, eikä painallus saa tuottaa
  // arvoa, joka torjutaan tallennuksessa.
  const askel = (delta: number) =>
    onChange(
      minutes === null
        ? MINUTE_STEP
        : Math.min(maxMinutes, Math.max(MINUTE_STEP, minutes + delta)),
    );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="border-reuna flex items-center overflow-hidden rounded-md border">
        <button
          type="button"
          onClick={() => askel(-MINUTE_STEP)}
          disabled={minutes === null || minutes <= MINUTE_STEP}
          aria-label={t('kesto.vahenna')}
          className="text-teksti-vaimea hover:text-teksti px-3 py-2 disabled:opacity-40"
        >
          <Minus className="size-4" aria-hidden="true" />
        </button>
        <span className="luvut text-teksti border-reuna min-w-20 border-x px-3 py-1.5 text-center text-sm font-medium">
          {minutes === null ? '–' : m.tunnit(minutes)}
        </span>
        <button
          type="button"
          onClick={() => askel(MINUTE_STEP)}
          disabled={minutes !== null && minutes >= maxMinutes}
          aria-label={t('kesto.lisaa')}
          className="text-teksti-vaimea hover:text-teksti px-3 py-2 disabled:opacity-40"
        >
          <Plus className="size-4" aria-hidden="true" />
        </button>
      </div>

      <div className="flex flex-wrap gap-1">
        {DURATION_PRESETS_HOURS.map((hours) => {
          const value = hours * 60;
          return (
            <button
              key={hours}
              type="button"
              onClick={() => onChange(value)}
              aria-pressed={minutes === value}
              className={[
                'luvut rounded-md border px-2.5 py-1 text-xs transition-colors',
                minutes === value
                  ? 'border-korostus bg-korostus-pehmea text-korostus font-medium'
                  : 'border-reuna text-teksti-vaimea hover:text-teksti hover:border-reuna-vahva',
              ].join(' ')}
            >
              {m.tunnit(value, false)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
